/* The role catalog (designs/canonical-roles.md CR1), ported from renderRoles
   (app.ts ~4683). One org-wide list every project describes responsibilities
   from — the name, the single-holder answer, the stamp of who recorded it and
   when, and the description in its honest states (a recorded line, or the red
   NOT RECORDED the migrated roles carry). The same authority that may add may
   correct (CR8), inline, with a pre-save tightening warning (CR15).

   Parity with the vanilla renderer is the spec (see CONTRACT.md). REST calls go
   through the bundle's own write actions (rolesPermissions.actions), each
   byte-identical to app.ts. The on-nav read (store.openRoles) already ran from
   selectNav; this view just renders from state. */
import type { JSX } from "react";
import { useAppState } from "../shell/store";
import { ViewFrame } from "../shell/ViewFrame";
import { LoadError } from "../shell/LoadError";
import { iPanel } from "../shell/helpText";
import { enabled, type Surface } from "../../../shared/features";
import { HELP } from "../../help";
import { nearMatches, type Role } from "../../../shared/roles";
import {
  cancelRoleEdit,
  openRoleEditor,
  patchRole,
  saveRole,
  toggleRetire,
} from "./rolesPermissions.actions";
import type { Store } from "../shell/store";
import type { ClientState } from "../shell/state";

/* the plain-text peer of the vanilla hint() attribute fragment — a title only
   when About Hermes is on this surface and the key is known, so JSX omits the
   attribute exactly where the vanilla ` title="…"` was empty (help-layer HL3) */
const titleFor = (surface: Surface, id: string): string | undefined =>
  enabled("about-hermes", surface) ? HELP[id] : undefined;

const contents = { display: "contents" } as const;

/* What a saved tightening contradicted (CR15) — reported, never a failure */
function RoleConflictLine({ state }: { state: ClientState }): JSX.Element | null {
  if (!state.roleConflicts.length) return null;
  const n = state.roleConflicts.length;
  return (
    <div className="list-error" id="role-conflicts">
      <span className="state unconfirmed">flagged</span>
      Saved. {n} project{n === 1 ? "" : "s"} now contradict this role's single-holder rule — the flag
      is on those projects, and nothing they recorded was changed.
    </div>
  );
}

/* The inline editor (CR12). It edits the role addressed by its ID — the name
   is just another field, and changing it never changes which role this is. */
function RoleEditor({
  state,
  store,
  role,
}: {
  state: ClientState;
  store: Store;
  role: Role;
}): JSX.Element {
  return (
    <li className="role-editing">
      <span className="slot-main">
        <div className="card-form">
          <label className="field">
            <span className="field-label">Name</span>
            <input
              id="edit-name"
              type="text"
              value={state.editRoleName}
              onChange={(e) => store.update({ editRoleName: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field-label">Description (one line)</span>
            <input
              id="edit-description"
              type="text"
              value={state.editRoleDescription}
              placeholder="Not recorded unless you say"
              onChange={(e) => store.update({ editRoleDescription: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field-label">How many may hold it</span>
            <select
              id="edit-cardinality"
              value={state.editRoleCardinality}
              onChange={(e) =>
                store.update({ editRoleCardinality: e.target.value as Role["cardinality"] })
              }
            >
              <option value="many">several people</option>
              <option value="one">one person only</option>
            </select>
          </label>
          <div className="form-actions">
            <button
              id="save-edit"
              className="btn small"
              onClick={() =>
                void patchRole(store, role.id, {
                  name: state.editRoleName,
                  description: state.editRoleDescription,
                  cardinality: state.editRoleCardinality,
                })
              }
            >
              Save changes
            </button>
            <button id="cancel-edit" className="btn ghost small" onClick={() => cancelRoleEdit(store)}>
              Cancel
            </button>
          </div>
        </div>
      </span>
    </li>
  );
}

/* A catalog row (CR5): name, single-holder answer, stamp, description in its
   honest states, and — for the authority — Edit/Retire. Retired stays IN the
   list, marked (CR16): retiring takes a role out of circulation, never out of
   the record. */
function RoleRow({
  state,
  store,
  role,
}: {
  state: ClientState;
  store: Store;
  role: Role;
}): JSX.Element {
  if (state.editingRoleId === role.id) return <RoleEditor state={state} store={store} role={role} />;
  const surface = state.me.surface;
  const held = role.cardinality === "one" ? "one person only" : "several people";
  const by = role.recorded.by ? `${role.recorded.by.name} · ` : "";
  return (
    <li>
      <span className="slot-main">
        <span className="slot-name" title={titleFor(surface, "role-cardinality")}>
          {role.name} · {held}
          {role.retired ? (
            <span className="state record-status status-archived">retired</span>
          ) : null}
          <span className="stamp">
            {by}
            {role.recorded.source}
          </span>
        </span>
        {role.description ? (
          <div className="slot-note">{role.description}</div>
        ) : (
          <div className="slot-note">
            <span className="state missing">not recorded</span>
          </div>
        )}
      </span>
      {state.catalogAuthority ? (
        <span className="card-actions">
          <button className="edit-role btn ghost small" onClick={() => openRoleEditor(store, role.id)}>
            Edit
          </button>
          <button
            className="retire-role btn ghost small"
            title={titleFor(surface, "role-retire")}
            onClick={() => void toggleRetire(store, role)}
          >
            {role.retired ? "Bring back" : "Retire"}
          </button>
        </span>
      ) : null}
    </li>
  );
}

/* Near-matches are shown, never enforced (CR4) — "Localization Lead" and
   "Localization SME" must both be addable, so only an exact name refuses. */
function NearMatchLine({ state }: { state: ClientState }): JSX.Element | null {
  const near = nearMatches(state.roles, state.roleQuery);
  if (!near.length) return null;
  return (
    <div className="slot-note" id="near-matches">
      Already in the catalog: {near.map((r) => r.name).join(", ")} — add yours anyway if it is a
      different role.
    </div>
  );
}

/* The Add-a-role form (CR8): only the catalog authority sees it. */
function AddRoleForm({ state, store }: { state: ClientState; store: Store }): JSX.Element {
  if (!state.catalogAuthority)
    return (
      <div className="slot-note" id="no-catalog-authority">
        Adding a role needs a committed project lead or project manager allocation, or the Admin
        organizer role.
      </div>
    );
  return (
    <section className="card confirmed" id="add-role">
      <div className="card-head">
        <div className="title">Add a role</div>
      </div>
      <div className="card-form">
        <label className="field">
          <span className="field-label">Name</span>
          <input
            id="role-name"
            type="text"
            value={state.roleQuery}
            placeholder="e.g. Localization Lead"
            onChange={(e) => store.update({ roleQuery: e.target.value })}
          />
        </label>
        <NearMatchLine state={state} />
        <label className="field">
          <span className="field-label">Description (one line)</span>
          <input
            id="role-description"
            type="text"
            value={state.roleDescription}
            placeholder="Not recorded unless you say"
            onChange={(e) => store.update({ roleDescription: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="field-label">How many may hold it</span>
          <select
            id="role-cardinality"
            value={state.roleCardinality}
            onChange={(e) => store.update({ roleCardinality: e.target.value as Role["cardinality"] })}
          >
            <option value="many">several people</option>
            <option value="one">one person only</option>
          </select>
        </label>
      </div>
      <div className="form-actions">
        <button
          id="save-role"
          className="btn small"
          disabled={state.saving}
          onClick={() => void saveRole(store)}
        >
          {state.saving ? "Saving…" : "Save role"}
        </button>
      </div>
    </section>
  );
}

export function RolesView(): JSX.Element {
  const { state, store } = useAppState();
  /* the catalog is this section's own fault domain: losing it is the only
     thing that costs the page (OH6) */
  if (state.loadError) return <LoadError what="roles" />;
  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>Roles.</b>
          <span>
            {state.roles.length} roles — one org-wide list every project describes responsibilities
            from.
          </span>
        </div>
      }
      footnote={
        "Roles come from this one canonical list — never free text, never hardcoded. Every entry carries who recorded it and when."
      }
    >
      {/* focusable so a rejection can be brought to the reader (UI/UX review) */}
      {state.listError ? (
        <p className="form-error" role="alert" tabIndex={-1}>
          {state.listError}
        </p>
      ) : null}
      <RoleConflictLine state={state} />
      <AddRoleForm state={state} store={store} />
      {/* the ⓘ concept panel — trusted help HTML, display:contents so the
          <details> sits as a sibling exactly as the vanilla innerHTML did */}
      <div
        style={contents}
        dangerouslySetInnerHTML={{ __html: iPanel(state.me.surface, "who-can-do-what") }}
      />
      <ul className="slots">
        {state.roles.map((role) => (
          <RoleRow key={role.id} state={state} store={store} role={role} />
        ))}
      </ul>
    </ViewFrame>
  );
}
